// ═══════════════════════════════════════════════════════════════════════════
// HOLDING BONUS MANAGER - TASK 2
// Tier-based reward bonuses for $DISTIL holders
// Hold more → Earn more! No locking required!
// ═══════════════════════════════════════════════════════════════════════════

export const HOLDING_TIERS = [
  { 
    threshold: 1_000_000, 
    bonus: 1.20, 
    name: "Diamond", 
    badge: "💎",
    color: "#b9f2ff",
    description: "+20% rewards + Revenue share",
    benefits: [
      "+20% bonus rewards",
      "Quarterly revenue share",
      "Early feature access",
      "Diamond badge flair"
    ]
  },
  { 
    threshold: 100_000, 
    bonus: 1.15, 
    name: "Gold", 
    badge: "🏆",
    color: "#FFD700",
    description: "+15% rewards + Early access",
    benefits: [
      "+15% bonus rewards",
      "Early feature access",
      "Priority support",
      "Gold badge flair"
    ]
  },
  { 
    threshold: 10_000, 
    bonus: 1.10, 
    name: "Silver", 
    badge: "🥈",
    color: "#C0C0C0",
    description: "+10% rewards + Priority support",
    benefits: [
      "+10% bonus rewards",
      "Priority support",
      "Silver badge flair"
    ]
  },
  { 
    threshold: 1_000, 
    bonus: 1.05, 
    name: "Bronze", 
    badge: "🥉",
    color: "#CD7F32",
    description: "+5% bonus rewards",
    benefits: [
      "+5% bonus rewards",
      "Bronze badge flair"
    ]
  },
  { 
    threshold: 0, 
    bonus: 1.00, 
    name: "None", 
    badge: "",
    color: "#64748b",
    description: "Hold 1,000+ $DISTIL to unlock bonuses!",
    benefits: []
  }
];

/**
 * Get holding tier based on balance
 */
export function getHoldingTier(distilBalance) {
  for (const tier of HOLDING_TIERS) {
    if (distilBalance >= tier.threshold) {
      return tier;
    }
  }
  return HOLDING_TIERS[HOLDING_TIERS.length - 1];
}

/**
 * Calculate bonus reward
 */
export function calculateBonusReward(baseReward, distilBalance) {
  const tier = getHoldingTier(distilBalance);
  const finalReward = baseReward * tier.bonus;
  const bonusAmount = finalReward - baseReward;
  
  return {
    baseReward,
    bonusAmount,
    finalReward,
    tier,
    bonusPercentage: ((tier.bonus - 1) * 100).toFixed(0)
  };
}

/**
 * Get next tier
 */
export function getNextTier(distilBalance) {
  for (let i = HOLDING_TIERS.length - 1; i >= 0; i--) {
    if (HOLDING_TIERS[i].threshold > distilBalance) {
      return HOLDING_TIERS[i];
    }
  }
  return null;
}

/**
 * Get progress to next tier
 */
export function getTierProgress(distilBalance) {
  const currentTier = getHoldingTier(distilBalance);
  const nextTier = getNextTier(distilBalance);
  
  if (!nextTier) {
    return {
      hasNextTier: false,
      currentTier,
      progress: 100,
      remaining: 0
    };
  }
  
  const currentThreshold = currentTier.threshold;
  const nextThreshold = nextTier.threshold;
  const range = nextThreshold - currentThreshold;
  const progress = ((distilBalance - currentThreshold) / range) * 100;
  
  return {
    hasNextTier: true,
    currentTier,
    nextTier,
    progress: Math.min(100, Math.max(0, progress)),
    remaining: Math.max(0, nextThreshold - distilBalance)
  };
}

/**
 * Demo balance management
 */
export function getDemoDistilBalance() {
  return parseFloat(localStorage.getItem('demo_distil_balance') || '5000');
}

export function setDemoDistilBalance(amount) {
  localStorage.setItem('demo_distil_balance', amount.toString());
}

export function getUserDistilBalance(walletBalance = null, isDemoMode = true) {
  if (isDemoMode) {
    return getDemoDistilBalance();
  }
  if (walletBalance && walletBalance.totalBalance) {
    return parseFloat(walletBalance.totalBalance) / 1_000_000_000;
  }
  return 0;
}

/**
 * Utility functions
 */
export function formatTierDisplay(tier) {
  if (tier.name === "None") return "No Tier";
  return `${tier.badge} ${tier.name}`;
}

export function getAllTiers() {
  return HOLDING_TIERS.slice(0, -1);
}

export default {
  HOLDING_TIERS,
  getHoldingTier,
  calculateBonusReward,
  getNextTier,
  getTierProgress,
  getDemoDistilBalance,
  setDemoDistilBalance,
  getUserDistilBalance,
  formatTierDisplay,
  getAllTiers
};
